import { select, selectAll, selectById } from '@commons/util/utilities/store/entity.select';
import { map, mergeMap, of, OperatorFunction, pipe } from 'rxjs';

import { ChannelType } from '@contact/upgrade';

import {
  BasketCategoryModel,
  BasketChannelModel,
  BasketColumnModel,
  BasketLabelModel,
  BasketModel,
  BasketStoreModel,
} from './basket.model';
import { BasketCategoryType, Sort } from './models/basket';

/**
 * get channel type selected
 */
export function selectOpen(): OperatorFunction<BasketStoreModel, boolean> {
  return map(s => s.open);
}

/**
 * get channel type selected
 */
export function selectChannelSelected(): OperatorFunction<BasketStoreModel, ChannelType> {
  return select(s => ChannelType[s.channelSelected]);
}

/**
 * get channel with given channel type
 * @param channelType channel type to found
 */
export function selectChannelByType<T extends BasketChannelModel>(channelType: ChannelType): OperatorFunction<BasketStoreModel, T> {
  return pipe(
    map(s => s.channels),
    selectById<T>(channelType)
  );
}

/**
 * get current channel selected
 */
export function selectCurrentChannel<T extends BasketChannelModel>(): OperatorFunction<BasketStoreModel, T> {
  return mergeMap(s => of(s).pipe(selectChannelByType<T>(s.channelSelected)));
}

/**
 * get all channels declared
 */
export function selectChannels<T extends BasketChannelModel>(): OperatorFunction<BasketStoreModel, T[]> {
  return pipe(
    map(s => s.channels),
    selectAll<T>()
  );
}

/**
 * get all channel types declared
 */
export function selectChannelTypes(): OperatorFunction<BasketStoreModel, ChannelType[]> {
  return pipe(
    selectChannels(),
    map(channels => channels.map(c => c.channel))
  );
}

/**
 * get category type selected in current channel
 */
export function selectCategorySelected(): OperatorFunction<BasketStoreModel, BasketCategoryType> {
  return pipe(
    selectCurrentChannel(),
    select(s => s.categorySelected)
  );
}

/**
 * get category with given type in current channel
 * @param category type to found
 */
export function selectCategoryByType<T extends BasketCategoryModel>(category: BasketCategoryType): OperatorFunction<BasketStoreModel, T> {
  return pipe(
    selectCurrentChannel(),
    map(s => s.categories),
    selectById<T>(category)
  );
}

/**
 * get current category selected in current channel
 */
export function selectCurrentCategory<T extends BasketCategoryModel>(): OperatorFunction<BasketStoreModel, T> {
  return mergeMap(s =>
    of(s).pipe(
      selectCategorySelected(),
      mergeMap(category => of(s).pipe(selectCategoryByType<T>(category)))
    )
  );
}

/**
 * get all categories declared in current channel
 */
export function selectCategories<T extends BasketCategoryModel>(): OperatorFunction<BasketStoreModel, T[]> {
  return pipe(
    selectCurrentChannel(),
    map(s => s.categories),
    selectAll<T>()
  );
}

/**
 * get all category types declared in current channel
 */
export function selectCategoryTypes(): OperatorFunction<BasketStoreModel, BasketCategoryType[]> {
  return pipe(
    selectCategories(),
    map(categories => categories.map(c => c.category))
  );
}

/**
 * get label type selected in current channel
 */
export function selectLabelSelected(): OperatorFunction<BasketStoreModel, string> {
  return pipe(
    selectCurrentChannel(),
    select(s => s.labelSelected)
  );
}

/**
 * get label with given type in current channel
 * @param label type to found
 */
export function selectLabelByType<T extends BasketLabelModel>(label: string): OperatorFunction<BasketStoreModel, T> {
  return pipe(
    selectCurrentChannel(),
    map(s => s.labels),
    selectById<T>(label)
  );
}

/**
 * get category from label with given type in current channel
 * @param label type to found
 */
export function selectCategoryByLabel<T extends BasketCategoryModel>(label: string): OperatorFunction<BasketStoreModel, T> {
  return mergeMap(s =>
    of(s).pipe(
      selectLabelByType(label),
      mergeMap(labelModel => of(s).pipe(selectCategoryByType<T>(labelModel.category)))
    )
  );
}

/**
 * get current label selected in current channel
 */
export function selectCurrentLabel<T extends BasketLabelModel>(): OperatorFunction<BasketStoreModel, T> {
  return mergeMap(s =>
    of(s).pipe(
      selectLabelSelected(),
      mergeMap(label => of(s).pipe(selectLabelByType<T>(label)))
    )
  );
}

/**
 * get all labels declared in current channel
 */
export function selectLabels<T extends BasketLabelModel>(channelType: ChannelType): OperatorFunction<BasketStoreModel, T[]> {
  return pipe(
    selectChannelByType(channelType),
    map(s => s.labels),
    selectAll<T>()
  );
}

/**
 * get label with given type in current channel
 * @param category type to found
 */
export function selectLabelByCategory<T extends BasketLabelModel>(
  channelType: ChannelType,
  category: BasketCategoryType
): OperatorFunction<BasketStoreModel, T[]> {
  return pipe(
    selectLabels<T>(channelType),
    map(labels => labels.filter(label => label.category === category))
  );
}

export function selectLabelCountByType(channelType: ChannelType, ...type: string[]): OperatorFunction<BasketStoreModel, number> {
  return pipe(
    selectLabels(channelType),
    map(labels => labels.filter(l => type.some(t => t === l.label))),
    map(filters => filters.reduce((acc, v) => acc + v.nbRequests, 0))
  );
}

export function selectLabelTotalCountByType(channelType: ChannelType, ...type: string[]): OperatorFunction<BasketStoreModel, number> {
  return pipe(
    selectLabels(channelType),
    map(labels => labels.filter(l => type.some(t => t === l.label))),
    map(filters => filters.reduce((acc, v) => acc + v.nbTotalRequests, 0))
  );
}

/**
 * get loading from current channel
 */
export function selectCurrentLoading(): OperatorFunction<BasketStoreModel, boolean> {
  return pipe(
    selectCurrentChannel(),
    select(s => s.loading)
  );
}

/**
 * get page size from current channel
 */
export function selectCurrentPageSize(): OperatorFunction<BasketStoreModel, number> {
  return pipe(
    selectCurrentChannel(),
    select(s => s.pageSize)
  );
}

/**
 * get current page from current label
 */
export function selectCurrentPage(): OperatorFunction<BasketStoreModel, number> {
  return pipe(
    selectCurrentLabel(),
    select(s => s.currentPage)
  );
}

/**
 * get timestamp from current label
 */
export function selectCurrentTs(): OperatorFunction<BasketStoreModel, number> {
  return pipe(
    selectCurrentLabel(),
    select(s => s.timestamp)
  );
}

/**
 * get number requests from current label
 */
export function selectCurrentNbRequests(): OperatorFunction<BasketStoreModel, number> {
  return pipe(
    selectCurrentLabel(),
    select(s => s.nbRequests)
  );
}

/**
 * get request from current label
 */
export function selectCurrentRequest<T extends BasketModel>(requestId: string): OperatorFunction<BasketStoreModel, T> {
  return pipe(
    selectCurrentLabel(),
    map(s => s.requests),
    selectById<T>(requestId)
  );
}

/**
 * get request list from current label
 */
export function selectCurrentRequests<T extends BasketModel>(): OperatorFunction<BasketStoreModel, T[]> {
  return pipe(
    selectCurrentLabel(),
    map(s => s.requests),
    selectAll<T>()
  );
}

/**
 * get show pagination boolean from current channel
 */
export function selectCurrentShowPagination(): OperatorFunction<BasketStoreModel, boolean> {
  return pipe(
    selectCurrentLabel(),
    select(s => s.showPagination)
  );
}

/**
 * get timestamp from current label
 */
export function selectCurrentSort(): OperatorFunction<BasketStoreModel, Sort> {
  return pipe(
    selectCurrentCategory(),
    select(s => s.sort)
  );
}

/**
 * get columns for category
 */
export function selectCurrentColumns<T extends BasketColumnModel>(): OperatorFunction<BasketStoreModel, T[]> {
  return pipe(
    selectCurrentCategory(),
    map(s => s.columns as T[])
  );
}

export function selectLabelsByChannelType<T extends BasketLabelModel>(channelType: ChannelType): OperatorFunction<BasketStoreModel, T[]> {
  return pipe(
    selectChannelByType(channelType),
    map(s => s.labels),
    selectAll<T>()
  );
}

export function selectLabelTotalCountByTypeAndChannel(
  channelType: ChannelType,
  ...type: string[]
): OperatorFunction<BasketStoreModel, number> {
  return pipe(
    selectLabelsByChannelType(channelType),
    map(labels => labels.filter(l => type.some(t => t === l.label))),
    map(filters => filters.reduce((acc, v) => acc + v.nbTotalRequests, 0))
  );
}
